import { signal, type Signal } from '@preact/signals';
import { useEffect } from 'preact/hooks';

import type { PictoCatalog, PictoContext } from '@amumin/api-client';
import { PICTO_FAITH_TAGS } from '@amumin/domain/profile';
import { t } from '@amumin/i18n';
import type { IconPickerHidden } from '@amumin/ui';

import { api } from '../api.js';
import { appText } from '../state/session.js';
import { PROFILE_SETTINGS_PATH, reasonGroupKey, reasonHeading, reasonText } from './profileText.js';
import { OPT_IN_NEVER, type HiddenPictos } from './suggest.js';

// Welke pictogrammen verborgen zijn (0024; GET /v1/app/pictos?context=...). De catalogus zelf is
// statisch (ui-core art.ts); de server zegt per context wat bij dit gezin uit het zicht staat en
// waarom. Eén verzoek per context, kort bewaard; een nieuwe kiezer haalt hem opnieuw op na 30 s.

const TTL_MS = 30_000;

const store: Record<PictoContext, Signal<PictoCatalog | null>> = {
  general: signal(null),
  food: signal(null),
};
const fetchedAt: Record<PictoContext, number> = { general: 0, food: 0 };
const pending: Partial<Record<PictoContext, Promise<void>>> = {};

async function load(context: PictoContext): Promise<void> {
  if (pending[context]) return pending[context];
  const run = api.app.food.profileCatalog
    .pictos(context)
    .then((c) => {
      store[context].value = c;
      fetchedAt[context] = Date.now();
    })
    .catch(() => {
      // Geen verbinding of geen recht: de geloofspictogrammen blijven uit het raster (pickerHidden),
      // de rest is zichtbaar; zoeken vindt alles (nooit blokkeren).
    })
    .finally(() => {
      delete pending[context];
    });
  pending[context] = run;
  return run;
}

/** De verborgen pictogrammen voor deze context (null tot het antwoord er is). */
export function usePictoCatalog(context: PictoContext): PictoCatalog | null {
  useEffect(() => {
    if (Date.now() - fetchedAt[context] > TTL_MS) void load(context);
  }, [context]);
  return store[context].value;
}

/**
 * Verborgen id's voor het titelvoorstel (suggest.ts): nieuwe opt-in-opties nooit, niet aangevinkte
 * gewoontes (golf 6b) ook nooit, de rest letterlijk.
 */
export function hiddenPictoIds(catalog: PictoCatalog | null): HiddenPictos {
  if (!catalog) return OPT_IN_NEVER;
  const out = new Map<string, 'never' | 'literal'>();
  for (const h of catalog?.hidden ?? []) {
    const kind = h.hidden_reason.kind;
    out.set(
      h.id,
      kind === 'not_chosen' || kind === 'practice' || kind === 'culture' ? 'never' : 'literal',
    );
  }
  return out;
}

/**
 * Zolang de server nog niets zei, of als het verzoek mislukte (review e r3): alle
 * geloofspictogrammen (PICTO_FAITH_TAGS: moskee, bidden, kerk, …) uit het raster, zoals de server
 * doet bij een gezin dat de geloofsvraag open liet. Zoeken vindt ze wel (onderaan, met reden); geen
 * teller, want wat er echt verborgen is, weten we nog niet. Zo ziet een neutraal gezin nooit even
 * een moskee in het raster.
 */
const UNKNOWN_HIDDEN: Omit<PictoCatalog, 'context'> = {
  hidden: Object.keys(PICTO_FAITH_TAGS).map((id) => ({
    id,
    hidden_reason: { kind: 'faith_open', faiths: [] },
  })),
  badge: 0,
};

/** Catalogus → de `hidden`-prop van de IconPicker (met teksten uit i18n). */
export function pickerHidden(catalog: PictoCatalog | null): IconPickerHidden | null {
  const known = catalog ?? UNKNOWN_HIDDEN;
  if (known.hidden.length === 0) return null;
  const a = appText.value;
  const p = a.profile;
  return {
    items: known.hidden.map((h) => ({
      id: h.id,
      groupKey: reasonGroupKey(h.hidden_reason),
      groupLabel: reasonHeading(p, h.hidden_reason, a.culture.hidden.groupFaithNone),
      reason: reasonText(p, h.hidden_reason),
    })),
    badge: known.badge,
    labels: {
      count: (n) => t(p.hidden.count, { count: n }),
      showAll: p.hidden.showAll,
      showLess: p.hidden.showLess,
      change: p.hidden.change,
      searchHidden: a.culture.hidden.searchHidden,
    },
    changeHref: PROFILE_SETTINGS_PATH,
  };
}
